import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteScrollSentinel } from '@/ui/hooks';
import {
    UIAvatar,
    UIEmptyState,
    UIErrorState,
    UIFilterGroup,
    UIFormInput,
    UIIcon,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIMultiFilterGroup,
    UIMultiFilterItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    UISection,
    UISpinner,
    UITableHeaderCell,
    UITableSortDirection,
    UIText,
    UITextButton,
    useUIConfirmationModal
} from '@/ui';
import styles from './teams-list.module.scss';
import { Team } from '@/api';
import { charcoal, red } from '@/ui/styles';
import { useFetchTeamCounts, useFetchTeamsPaginated } from '../queries';
import { useDeleteTeamAction } from '../actions';
import { useCompaniesTranslation } from '../hooks';
import { TaskFeaturesIcons } from './task-features-icons';
import { useDebouncedValue } from '@/common/hooks';
import { parseSortOrder, setOrDeleteParam } from '@/common/util';
import { useSearchParams } from 'react-router';

type SortBy = 'name';

const TASK_SETTING_IDS = ['arc', 'pin'];

function parseTaskSettings(value: string | null): Map<string, boolean> {
    const settings = new Map<string, boolean>();
    // Anything unrecognised is dropped rather than trusted — the query string
    // is user-editable.
    value?.split(',').forEach((setting) => {
        if (TASK_SETTING_IDS.includes(setting)) {
            settings.set(setting, true);
        }
    });
    return settings;
}

function serialiseTaskSettings(settings: Map<string, boolean>): string {
    return Array.from(settings.entries())
        .filter(([, selected]) => selected)
        .map(([setting]) => setting)
        .join(',');
}

type TeamsListProps = {
    companyId: string;
    onTeamPress: (team: Team) => void;
    onTeamMembersPress: (team: Team) => void;
};
export function TeamsList(props: TeamsListProps): ReactElement {
    const { companyId, onTeamPress, onTeamMembersPress } = props;

    const { t } = useCompaniesTranslation();

    // Seeded from the query string, then written back to it below. Read once,
    // on mount: after that this component owns the values, and re-reading would
    // fight the user as they type.
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(
        () => searchParams.get('q') ?? ''
    );
    const debouncedSearch = useDebouncedValue(searchQuery.trim());
    const [taskSettingsFilter, setTaskSettingsFilter] = useState<
        Map<string, boolean>
    >(() => parseTaskSettings(searchParams.get('settings')));

    // Only one column sorts, so the column itself never needs saying — the
    // direction is the whole of it.
    const [sortBy, setSortBy] = useState<SortBy>('name');
    const [sortDirection, setSortDirection] = useState<UITableSortDirection>(
        () => parseSortOrder(searchParams.get('order'))
    );

    // Mirrored into the query string so opening a team and coming back — or a
    // refresh, or a shared link — lands on the same view. Replaces rather than
    // pushes: a search would otherwise leave a history entry per keystroke. The
    // debounced search is written, not the raw one, so the URL matches the rows
    // actually on screen.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        setOrDeleteParam(next, 'q', debouncedSearch);
        setOrDeleteParam(
            next,
            'settings',
            serialiseTaskSettings(taskSettingsFilter)
        );
        setOrDeleteParam(
            next,
            'order',
            sortDirection === 'asc' ? '' : sortDirection
        );

        // Guarded: `searchParams` changes identity on every navigation, and
        // setting it unconditionally would navigate in a loop.
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [
        searchParams,
        setSearchParams,
        debouncedSearch,
        taskSettingsFilter,
        sortDirection
    ]);

    // Feeds the "can't delete the last team" guard on each row and the member
    // count each row shows. The total has to be company-wide — the loaded page
    // is a window, and counting it would let a user delete the last team
    // whenever the rest hadn't loaded.
    const {
        counts,
        status: countsStatus,
        refetch: refetchCounts
    } = useFetchTeamCounts(companyId);

    const {
        teams,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchTeamsPaginated({
        companyId,
        search: debouncedSearch,
        // Unchecked means "no constraint", not "must be false".
        arcEnabled: taskSettingsFilter.get('arc') ? true : undefined,
        pinEnabled: taskSettingsFilter.get('pin') ? true : undefined,
        sortBy,
        sortOrder: sortDirection
    });

    // The rows and the count that guards their actions are shown together —
    // rendering rows first would briefly offer Remove against an unknown total.
    const listStatus =
        status === 'error' || countsStatus === 'error'
            ? 'error'
            : status === 'pending' || countsStatus === 'pending'
              ? 'pending'
              : 'success';

    const { ref: bottomRef } = useInfiniteScrollSentinel({
        hasNextPage,
        isFetchingNextPage,
        onLoadMore: fetchNextPage
    });

    const selectedCount = useMemo(() => {
        let count = 0;

        taskSettingsFilter.forEach((value) => {
            if (value) {
                count++;
            }
        });

        return count;
    }, [taskSettingsFilter]);

    function clearSearch() {
        setSearchQuery('');
        setTaskSettingsFilter(new Map());
    }

    // The filter callback identifies a group by its (translated) name, so both
    // sides read from here rather than repeating the string.
    const groupNames = useMemo(
        () => ({
            taskSettings: t('components.teams_list.filter_task_settings')
        }),
        [t]
    );

    const groups: UIMultiFilterGroup[] = useMemo(() => {
        const items: UIMultiFilterGroup[] = [
            {
                name: groupNames.taskSettings,
                type: 'multi',
                items: [
                    {
                        name: t('components.teams_list.filter_arc'),
                        id: 'arc',
                        selected: taskSettingsFilter.get('arc') ?? false
                    },
                    {
                        name: t('components.teams_list.filter_pin'),
                        id: 'pin',
                        selected: taskSettingsFilter.get('pin') ?? false
                    }
                ]
            }
        ];

        return items;
    }, [taskSettingsFilter, groupNames, t]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                <UIFilterGroup>
                    <UIMultiFilterItem
                        icon="sliders"
                        text={t('shared.labels.filter')}
                        groups={groups}
                        selectedCount={selectedCount}
                        onChanged={(newGroups) => {
                            newGroups.forEach((group) => {
                                if (group.name === groupNames.taskSettings) {
                                    const newSelectedValues = new Map<
                                        string,
                                        boolean
                                    >();
                                    group.items.forEach((item) => {
                                        newSelectedValues.set(
                                            item.id,
                                            item.selected
                                        );
                                    });
                                    setTaskSettingsFilter(newSelectedValues);
                                }
                            });
                        }}
                    />
                </UIFilterGroup>
                <UIFormInput
                    value={searchQuery}
                    placeholder={t('shared.labels.search')}
                    height={20}
                    onChange={setSearchQuery}
                />
            </div>
            {listStatus === 'pending' && <UILoadingState />}
            {listStatus === 'error' && (
                <UIErrorState
                    title={t('shared.errors.load_teams')}
                    onRetry={() => {
                        refetch();
                        refetchCounts();
                    }}
                />
            )}
            {listStatus === 'success' && teams.length === 0 && (
                <UIEmptyState
                    description={t('shared.labels.no_results')}
                    buttonText={t('shared.labels.clear_search')}
                    emoji="😔"
                    onClick={clearSearch}
                />
            )}
            {listStatus === 'success' && teams.length > 0 && (
                <UISection>
                    <table className={styles.userTable}>
                        <thead>
                            <TeamTableHeader
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                onClick={(newSortBy, newSortDirection) => {
                                    setSortBy(newSortBy);
                                    setSortDirection(newSortDirection);
                                }}
                            />
                        </thead>
                        <tbody>
                            {teams.map((team) => (
                                <TeamTableRow
                                    key={team.id}
                                    team={team}
                                    teamsCount={counts?.total ?? 0}
                                    memberCount={counts?.teams[team.id] ?? 0}
                                    onPress={() => {
                                        onTeamPress(team);
                                    }}
                                    onMembersPress={() => {
                                        onTeamMembersPress(team);
                                    }}
                                />
                            ))}
                            {hasNextPage && (
                                <tr ref={bottomRef}>
                                    <td
                                        colSpan={4}
                                        style={{
                                            textAlign: 'center',
                                            padding: 8
                                        }}
                                    >
                                        {isFetchingNextPage && (
                                            <UISpinner color={charcoal} />
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </UISection>
            )}
        </div>
    );
}

function TeamTableHeader(props: {
    sortBy: SortBy;
    sortDirection: UITableSortDirection;
    onClick: (sortBy: SortBy, sortDirection: UITableSortDirection) => void;
}): ReactElement {
    const { sortBy, sortDirection, onClick } = props;
    const { t } = useCompaniesTranslation();
    return (
        <tr>
            <UITableHeaderCell
                text={t('shared.labels.name')}
                sortable
                sortDirection={sortBy === 'name' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('name', newSortDirection);
                }}
            />
            {/* Not sortable: the list is paged by the server, which only sorts
                by name, and sorting the loaded page alone would be a lie. */}
            <UITableHeaderCell
                text={t('components.teams_list.column_members')}
                tooltip={t('components.teams_list.column_members_tooltip')}
            />
            <UITableHeaderCell
                text={t('components.teams_list.column_task_settings')}
                tooltip={t(
                    'components.teams_list.column_task_settings_tooltip'
                )}
            />
            <th />
        </tr>
    );
}

function TeamTableRow(props: {
    team: Team;
    teamsCount: number;
    memberCount: number;
    onPress: () => void;
    onMembersPress: () => void;
}): ReactElement {
    const { team, teamsCount, memberCount, onPress, onMembersPress } = props;

    const { t } = useCompaniesTranslation();

    const popoverRef = useRef<UIPropoverRef>(null);

    const deleteTeamAction = useDeleteTeamAction({
        team,
        teamsCount
    });

    const confirmationModal = useUIConfirmationModal();

    return (
        <tr className={styles.tableRow} onClick={onPress}>
            <td>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8
                    }}
                >
                    <UIAvatar img={team.image} alt={team.name} size={44} />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4
                        }}
                    >
                        <UIText medium>{team.name}</UIText>
                    </div>
                </div>
            </td>
            <td>
                <MembersCell count={memberCount} onClick={onMembersPress} />
            </td>
            <td>
                <TaskSettingsCell
                    pin={team.settings.pinEnabled}
                    arc={team.settings.arcEnabled}
                />
            </td>
            <td>
                <UIPopover
                    ref={popoverRef}
                    placement="auto"
                    popoverPadding={4}
                    trigger="click"
                    triggerNode={
                        <button className={styles.removeButtonStyle}>
                            <UIPressableAreaButtonLess width={16} height={16}>
                                <UIIcon
                                    name={'more-vertical'}
                                    size={16}
                                    color={charcoal}
                                />
                            </UIPressableAreaButtonLess>
                        </button>
                    }
                >
                    <UIMenu>
                        <UIMenuItem
                            text={t('shared.labels.remove')}
                            leftIconName="trash-2"
                            textColor={red}
                            loading={deleteTeamAction.loading}
                            disabled={!deleteTeamAction.isAllowed}
                            disabledTooltip={deleteTeamAction.reason}
                            onClick={(e) => {
                                e.stopPropagation();
                                popoverRef.current?.close();
                                confirmationModal.open({
                                    title: t(
                                        'components.teams_list.remove_team_confirm_title'
                                    ),
                                    confirmText: t(
                                        'components.teams_list.remove_team_confirm_button'
                                    ),
                                    confirmVariant: 'danger',
                                    cancelText: t('shared.labels.cancel'),
                                    onConfirm: () => {
                                        confirmationModal.setLoading(true);
                                        deleteTeamAction
                                            .deleteTeam()
                                            .catch(() => {}) // Error handled in the action
                                            .finally(() => {
                                                confirmationModal.close();
                                            });
                                    },
                                    onCancel: () => {
                                        confirmationModal.close();
                                    }
                                });
                            }}
                        />
                    </UIMenu>
                </UIPopover>
            </td>
        </tr>
    );
}

function MembersCell(props: {
    count: number;
    onClick: () => void;
}): ReactElement {
    const { count, onClick } = props;

    const { t } = useCompaniesTranslation();

    return (
        <UITextButton
            variant="primary"
            underline
            onClick={(e) => {
                // The row opens the team; this opens its Members tab.
                e.stopPropagation();
                onClick();
            }}
        >
            {t('components.teams_list.member_count', { count })}
        </UITextButton>
    );
}

function TaskSettingsCell(props: { arc: boolean; pin: boolean }): ReactElement {
    const { arc, pin } = props;

    return <TaskFeaturesIcons pin={pin} arc={arc} />;
}
